import type { ReleaseNotesEntry } from './interfaces/release-notes-entry.interface';
import type { ReleaseNotesCategory } from './interfaces/release-notes-category.type';
import type { ReleaseNotesSection } from './interfaces/release-notes-section.interface';

/**
 * Parser for frontend/RELEASE-NOTES.md, the end-user release notes in Keep-a-Changelog form.
 *
 * Format: everything before the first `## ` line is the header and is ignored. `## [Unreleased]`
 * collects drafts and is skipped. Every other `## ` line must be `## [X.Y.Z] - YYYY-MM-DD`; below it
 * `### Neu`, `### Verbessert` or `### Behoben`, each followed by `- ` bullets.
 *
 * Lines are matched raw: the only trimming is the blank-line test. Trimming anything else would
 * silently swallow a stray `\r` or byte-order mark and hide a malformed file.
 */

const UNRELEASED_HEADING = '## [Unreleased]';
const VERSION_HEADING = /^## \[(\d+)\.(\d+)\.(\d+)\] - (\d{4}-\d{2}-\d{2})$/;
const CATEGORIES: readonly ReleaseNotesCategory[] = ['Neu', 'Verbessert', 'Behoben'];

/**
 * Parses the release-notes markdown into its released entries, in file order.
 * Throws with a message naming the line when the file does not follow the format.
 */
export function parseReleaseNotes(markdown: string): ReleaseNotesEntry[] {
    const lines = markdown.replace(/^﻿/, '').replace(/\r\n/g, '\n').split('\n');
    const entries: ReleaseNotesEntry[] = [];
    let current: ReleaseNotesEntry | null = null;
    let section: ReleaseNotesSection | null = null;
    let inHeader = true;
    let inUnreleased = false;

    const closeEntry = (): void => {
        if (current === null) {
            return;
        }
        if (current.sections.length === 0) {
            throw new Error(`release notes: entry ${current.version} has no items`);
        }
        for (const s of current.sections) {
            if (s.items.length === 0) {
                throw new Error(`release notes: category ${s.category} in ${current.version} has no items`);
            }
        }
        entries.push(current);
        current = null;
        section = null;
    };

    lines.forEach((line, index) => {
        const lineNo = index + 1;

        if (line.startsWith('## ')) {
            inHeader = false;
            closeEntry();
            if (line === UNRELEASED_HEADING) {
                inUnreleased = true;
                return;
            }
            const match = VERSION_HEADING.exec(line);
            if (match === null) {
                throw new Error(`release notes: bad version heading on line ${lineNo}: ${JSON.stringify(line)}`);
            }
            inUnreleased = false;
            current = { version: `${match[1]}.${match[2]}.${match[3]}`, date: match[4], sections: [] };
            return;
        }

        if (inHeader || inUnreleased || line.trim() === '') {
            return;
        }

        const entry = current as ReleaseNotesEntry | null;
        if (entry === null) {
            return;
        }

        if (line.startsWith('### ')) {
            const category = line.slice(4) as ReleaseNotesCategory;
            if (!CATEGORIES.includes(category)) {
                throw new Error(`release notes: unknown category on line ${lineNo}: ${JSON.stringify(line)}`);
            }
            section = { category, items: [] };
            entry.sections.push(section);
            return;
        }

        if (line.startsWith('- ')) {
            if (section === null) {
                throw new Error(`release notes: bullet before a category on line ${lineNo}: ${JSON.stringify(line)}`);
            }
            section.items.push(line.slice(2));
            return;
        }

        throw new Error(`release notes: unexpected line ${lineNo}: ${JSON.stringify(line)}`);
    });

    closeEntry();
    return entries;
}

/** The version of the first released entry — the app version — or null when there is none. */
export function currentVersion(entries: readonly ReleaseNotesEntry[]): string | null {
    return entries.length > 0 ? entries[0].version : null;
}

/** Compares two X.Y.Z versions numerically; positive when a is newer than b. */
export function compareVersions(a: string, b: string): number {
    const left = a.split('.').map(Number);
    const right = b.split('.').map(Number);
    for (let i = 0; i < 3; i++) {
        const diff = (left[i] ?? 0) - (right[i] ?? 0);
        if (diff !== 0) {
            return diff;
        }
    }
    return 0;
}

/**
 * Throws unless the entries are ordered newest first: versions strictly descending and dates not
 * increasing downwards. Used by the guard on the real file, not at runtime.
 */
export function assertDescending(entries: readonly ReleaseNotesEntry[]): void {
    for (let i = 1; i < entries.length; i++) {
        const newer = entries[i - 1];
        const older = entries[i];
        if (compareVersions(newer.version, older.version) <= 0) {
            throw new Error(`release notes: versions not strictly descending: ${newer.version} before ${older.version}`);
        }
        if (newer.date < older.date) {
            throw new Error(`release notes: date increases downwards: ${newer.date} before ${older.date}`);
        }
    }
}
